/**
 * Stories — editorially-curated travel articles surfaced on the trips home of both clients
 * (`@elara/client` web + `@elara/mobile`). A story is an ordered list of content blocks (prose, entity
 * references, images, section headers, maps) that render into an App-Store-"Story"-style article: a
 * condensed card on the home rail that opens the full article (web: a routed page; mobile: a screen).
 *
 * Stories are written and published in the admin and read from Firestore (`stories/{id}`, see
 * `StoryModelSpec`) by `StoriesState`. Their shape lives in `@elara/domain`; this module re-exports it
 * so consumers keep a single import, and holds the one thing that stays in code: the author registry.
 */
import type { IconName } from '#ui-contracts/icon-name.js';
import type { StoryAuthorId } from '@elara/domain/models/schemas/story-schemas';

import { STORY_SECTION_ICONS } from '@elara/domain/models/schemas/story-schemas';

export type {
  Story,
  StoryAuthorId,
  StoryBlock,
  StoryEntityRef,
  StoryImage,
  StoryImageAsset,
  StoryImageVariant,
  StoryItemType,
} from '@elara/domain/models/schemas/story-schemas';

export interface StoryAuthor {
  id: StoryAuthorId;
  name: string;
  /** Shown under the name (e.g. "Travel Specialist"). */
  role: string;
}

/**
 * Author registry. The avatar image is resolved per-platform from the id (`natalie` / `shannon`):
 * web → `/team/${id}.jpg`, mobile → a bundled `require` map. Keeps this file platform-neutral.
 */
export const STORY_AUTHORS: Record<StoryAuthorId, StoryAuthor> = {
  natalie: { id: 'natalie', name: 'Natalie', role: 'Co-Founder of Elara' },
  shannon: { id: 'shannon', name: 'Shannon', role: 'Head of Travel at Elara' },
};

/** Fails to compile if the admin's section icons ever include a name the icon components can't draw. */
const SECTION_ICONS_ARE_ICON_NAMES: readonly IconName[] = STORY_SECTION_ICONS;
void SECTION_ICONS_ARE_ICON_NAMES;
